import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Clock,
  X,
  Compass,
  CheckCircle2,
  Calendar,
  MapPin,
  Sparkles,
  Award,
  Activity,
  Zap,
  Info,
  Layers,
  ChevronRight,
  HelpCircle,
  RotateCw,
  AlertTriangle
} from 'lucide-react';
import { Language, CompleteKundali } from '../types/kundali';
import { STRUCTURAL_QUESTIONS, RectificationEvent } from '../utils/birthTimeRectificationEngine';
import { executeBtrSearch } from '../utils/btrSearchOrchestrator';
import { TwoAxisBirthUncertainty, BtrLifeEventInput } from '../types/btr';
import { formatOrdinal } from '../utils/dateUtils';
import { UserProfile } from '../lib/firebase';
import { UserRole } from '../types/user';
import { isSuperAdminUser } from '../utils/rbac';
import { persistBtrEventsForProfile } from '../utils/btrEventStorage';

interface BtrDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  kundali?: CompleteKundali;
  onApplyRectifiedKundali?: (rectifiedKundali: CompleteKundali) => void;
  lang: Language;
  currentUser?: UserProfile | null;
}

export const BtrDrawer: React.FC<BtrDrawerProps> = ({
  isOpen,
  onClose,
  kundali,
  onApplyRectifiedKundali,
  lang,
  currentUser
}) => {
  const isHi = lang === 'hi';

  const isProOrAcharyaOrAdmin = React.useMemo(() => {
    try {
      const storedRole = localStorage.getItem('vj_active_role');
      if (storedRole === 'PRO' || storedRole === 'ACHARYA' || storedRole === 'MANAGEMENT') {
        return true;
      }
      if (currentUser) {
        const role = currentUser.activeRole || currentUser.role;
        if (
          role === UserRole.PRO_ASTROLOGER ||
          role === UserRole.ADVANCED_ASTROLOGY_USER ||
          role === UserRole.CONSULTING_ASTROLOGER ||
          role === UserRole.SUPER_ADMIN ||
          role === UserRole.ADMIN ||
          (currentUser as any).baseRole === UserRole.SUPER_ADMIN ||
          isSuperAdminUser(currentUser)
        ) {
          return true;
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }, [currentUser]);

  const birthDetails = kundali?.birthDetails || {
    name: 'Sample Native',
    gender: 'male',
    date: '1992-04-23',
    time: '12:00:00',
    latitude: 28.6139,
    longitude: 77.2090,
    placeName: 'New Delhi, Delhi, India',
    timezoneOffsetMinutes: 330
  };

  const recordedBirthTime = birthDetails.originalTime || null;
  const workingBirthTime = birthDetails.workingTime || birthDetails.time || null;

  // Interview Questions State (Direct start)
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState<number>(0);
  const [precision, setPrecision] = useState<'Exact' | 'Month/Year' | 'Year Only'>('Month/Year');
  const [dateVal, setDateVal] = useState<string>('');
  const [locationVal, setLocationVal] = useState<string>('');
  const [descVal, setDescVal] = useState<string>('');
  const [justSaved, setJustSaved] = useState<boolean>(false);
  const [showExitConfirm, setShowExitConfirm] = useState<boolean>(false);
  const [isRecalculating, setIsRecalculating] = useState<boolean>(false);
  const [recalculationOutcome, setRecalculationOutcome] = useState<string | null>(null);

  const handleRecalculateBtr = () => {
    if (!kundali) return;
    setIsRecalculating(true);
    setRecalculationOutcome(null);
    try {
      const bTime = birthDetails.time || '12:00:00';
      const bDate = birthDetails.date || '1992-04-23';
      const uncertainty: TwoAxisBirthUncertainty = {
        axisATime: {
          earliestLocalTime: '11:00:00',
          latestLocalTime: '13:00:00',
          reportedLocalTime: bTime,
          timeSource: 'Family recollection',
          precisionLevel: 'Minutes'
        },
        axisBDate: {
          earliestLocalDate: bDate,
          latestLocalDate: bDate,
          reportedLocalDate: bDate,
          certainty: 'EXACT',
          dateSource: 'Civil birth certificate'
        },
        location: {
          latitude: birthDetails.latitude || 28.6139,
          longitude: birthDetails.longitude || 77.2090,
          placeName: birthDetails.placeName || 'New Delhi, India',
          timezoneId: (birthDetails as any).timezoneId || 'Asia/Kolkata',
          timezoneOffsetMinutes: birthDetails.timezoneOffsetMinutes || 330
        }
      };

      const mappedEvents: BtrLifeEventInput[] = events.map((e, idx) => ({
        id: e.id || `evt-${idx}`,
        category: (e.category as any) || 'OTHER',
        date: e.date,
        tier: (e.date && e.date.length === 10) ? 'TIER_A' : 'TIER_B',
        description: e.description,
        location: e.location
      }));

      const searchResult = executeBtrSearch(uncertainty, mappedEvents);

      if (searchResult.convergentPlateaus && searchResult.convergentPlateaus.length === 1) {
        const plateau = searchResult.convergentPlateaus[0];
        const intervalStr = `${plateau.plateauInterval.startLocalTime} – ${plateau.plateauInterval.endLocalTime}`;
        setRecalculationOutcome(
          isHi
            ? `प्रमाणित शोधन अंतराल: ${intervalStr} (समर्थित क्लस्टर: ${plateau.independentClustersSupportedCount})। शून्य स्वतः-दत्तक ग्रहण: औपचारिक पुष्टि हेतु ज्योतिषी से परामर्श करें।`
            : `Evaluated Rectified Interval: ${intervalStr} (Supported Clusters: ${plateau.independentClustersSupportedCount}). Zero auto-adoption: formal confirmation requires astrologer consultation.`
        );
        if (onApplyRectifiedKundali) {
          onApplyRectifiedKundali({
            ...kundali,
            birthDetails: {
              ...kundali.birthDetails,
              btrStatus: 'RECTIFIED_INTERVAL',
              rectifiedInterval: intervalStr,
              workingTime: undefined,
              confirmedTime: undefined
            }
          });
        }
      } else {
        setRecalculationOutcome(
          isHi
            ? `गणना पूर्ण: ${searchResult.allEvaluatedPlateaus.length} अंतराल जाँचे गए। स्थिति: ${searchResult.outcomeStatus}`
            : `Calculation complete: ${searchResult.allEvaluatedPlateaus.length} plateaus evaluated. Status: ${searchResult.outcomeStatus}`
        );
      }
    } catch (err: any) {
      setRecalculationOutcome(`Recalculation error: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsRecalculating(false);
    }
  };

  // Immutable profile identity for isolated BTR storage
  // Only persist event data when the chart has a stable profile identity.
  // Birth details or display names alone are not a safe identity for private event storage.
  const profileStorageId = useMemo(() => {
    const explicitId = kundali?.id || (kundali?.birthDetails as any)?.profileId;
    // Guest charts are ephemeral: never persist their event data to shared browser storage.
    return currentUser?.uid && explicitId ? `${currentUser.uid}:${String(explicitId)}` : null;
  }, [kundali?.id, (kundali?.birthDetails as any)?.profileId, currentUser?.uid]);

  const loadEventsForProfile = (profileId: string | null): RectificationEvent[] => {
    if (!kundali || !profileId) return [];
    if (kundali.birthDetails.rectificationEvents?.length) {
      return kundali.birthDetails.rectificationEvents;
    }
    try {
      const saved = localStorage.getItem(`astroveda_btr_events_${profileId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {}
    return [];
  };

  const [events, setEvents] = useState<RectificationEvent[]>(() => loadEventsForProfile(profileStorageId));
  const [eventsProfileStorageId, setEventsProfileStorageId] = useState<string | null>(profileStorageId);

  // On chart/profile changes, load that profile's events before allowing persistence.
  useEffect(() => {
    if (eventsProfileStorageId === profileStorageId) return;
    setEvents(loadEventsForProfile(profileStorageId));
    setEventsProfileStorageId(profileStorageId);
  }, [profileStorageId, eventsProfileStorageId, kundali?.birthDetails?.rectificationEvents]);

  useEffect(() => {
    if (!kundali || !profileStorageId || eventsProfileStorageId !== profileStorageId) return;
    persistBtrEventsForProfile(profileStorageId, events);
  }, [events, profileStorageId, eventsProfileStorageId, kundali]);

  // Track initial state of active question inputs to determine dirty state
  const [initialQuestionState, setInitialQuestionState] = useState<{
    dateVal: string;
    locationVal: string;
    descVal: string;
    precision: 'Exact' | 'Month/Year' | 'Year Only';
  }>({
    dateVal: '',
    locationVal: '',
    descVal: '',
    precision: 'Month/Year'
  });

  // Check if current input fields have uncommitted edits (dirty checking)
  const hasChanges = useMemo(() => {
    return (
      dateVal.trim() !== initialQuestionState.dateVal.trim() ||
      locationVal.trim() !== initialQuestionState.locationVal.trim() ||
      descVal.trim() !== initialQuestionState.descVal.trim() ||
      precision !== initialQuestionState.precision
    );
  }, [dateVal, locationVal, descVal, precision, initialQuestionState]);

  // Intercept close requests: if there are unsaved edits or verified events, show confirmation dialog
  const handleRequestClose = () => {
    if (hasChanges || events.length > 0) {
      setShowExitConfirm(true);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    // Guest / unidentified profiles are ephemeral and must never write shared storage.
    persistBtrEventsForProfile(profileStorageId, events);
  }, [events, profileStorageId]);

  const handleAddEventFromQuestion = () => {
    if (!dateVal) return;
    const currentQuestion = STRUCTURAL_QUESTIONS[currentQuestionIdx];
    let formattedDate = dateVal;
    if (precision === 'Year Only') {
      formattedDate = `${dateVal}-06-15`;
    } else if (precision === 'Month/Year') {
      formattedDate = `${dateVal}-15`;
    }

    const newEvent: RectificationEvent = {
      id: 'evt-' + Date.now(),
      category: currentQuestion.category,
      description: descVal || `Milestone for ${currentQuestion.category.toLowerCase()}` + (locationVal ? ` in ${locationVal}` : ''),
      date: formattedDate,
      datePrecision: precision,
      location: locationVal || undefined,
      importance: 'High',
      reliability: precision === 'Exact' ? 'High' : 'Medium',
      astrologicalHouses: currentQuestion.houses,
      planets: currentQuestion.planets,
      vargas: currentQuestion.vargas
    };

    // If an event for this category already exists, update it non-destructively, else add new
    setEvents(prev => {
      const existingIdx = prev.findIndex(e => e.category === currentQuestion.category);
      if (existingIdx !== -1) {
        const copy = [...prev];
        copy[existingIdx] = { ...copy[existingIdx], ...newEvent, id: copy[existingIdx].id };
        return copy;
      }
      return [...prev, newEvent];
    });

    setJustSaved(true);
    setDateVal('');
    setLocationVal('');
    setDescVal('');
    setInitialQuestionState({
      dateVal: '',
      locationVal: '',
      descVal: '',
      precision: 'Month/Year'
    });
  };



  if (!isOpen || !kundali) return null;

  return (
    <AnimatePresence>
      <div
        id="btr-drawer-backdrop"
        className="fixed inset-0 z-[100] bg-[#121E1C]/60 backdrop-blur-xs flex justify-end"
        onClick={handleRequestClose}
      >
        <motion.div
          id="btr-drawer-panel"
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 26, stiffness: 240 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-3xl bg-astro-bg border-l border-astro-border shadow-2xl h-full flex flex-col overflow-hidden"
        >
          {/* Drawer Header */}
          <div className="p-4 sm:p-5 border-b border-astro-border bg-[#FAF6ED] flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-astro-primary text-astro-bg flex items-center justify-center shadow-xs">
                <Clock className="w-5 h-5 text-astro-accent" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base sm:text-lg font-bold text-astro-primary font-serif tracking-tight">
                    {isHi ? 'जन्म काल शोधन (BTR Studio)' : 'Janma Kala Shodhana (BTR Studio)'}
                  </h2>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-astro-primary/10 text-astro-primary border border-astro-primary/20">
                    V6.5
                  </span>
                </div>
                <p className="text-xs text-[#52635F]">
                  {isHi
                    ? 'जीवन-घटना साक्ष्य दर्ज करें और प्रमाणित V6.5 बीटीआर इंजन द्वारा समय सत्यापन करें।'
                    : 'Record life-event evidence and evaluate candidate plateaus with the verified V6.5 BTR engine.'}
                </p>
              </div>
            </div>

            <button
              onClick={handleRequestClose}
              className="p-2 rounded-xl bg-astro-bg hover:bg-astro-secondary-surface text-astro-secondary-text hover:text-astro-text transition-colors border border-astro-border cursor-pointer"
              aria-label="Close BTR Drawer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable Content Body */}
          <div className="p-4 sm:p-5 overflow-y-auto flex-1 space-y-4">
            <div role="alert" className="p-3.5 bg-emerald-50 border border-emerald-300 rounded-xl text-xs text-emerald-950">
              <strong>V6.5 Certified BTR Engine Active:</strong> Life-event alignment analysis evaluates candidate continuous plateaus with zero auto-adoption. Formal confirmation requires an Acharya consultation. Stored birth details remain the immutable baseline.
            </div>
            {/* Active Native Dossier Context Strip — 4-State Epistemic Display */}
            <div className="p-3.5 bg-[#FAF6ED] border border-astro-border rounded-xl space-y-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-astro-primary/10 text-astro-primary flex items-center justify-center font-bold text-xs">
                    {birthDetails.name ? birthDetails.name.charAt(0).toUpperCase() : 'N'}
                  </div>
                  <div>
                    <div className="text-xs font-bold text-astro-primary">
                      {birthDetails.name || 'Current Native'}
                    </div>
                    <div className="text-[11px] text-[#52635F] flex items-center gap-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-astro-accent" />
                        {birthDetails.date}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-astro-accent" />
                        {birthDetails.placeName || 'Indore, MP'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {birthDetails.btrStatus === 'STALE' ? (
                    <span className="text-[10px] font-mono font-bold px-2 py-1 rounded bg-amber-100 text-amber-900 border border-amber-400 flex items-center gap-1 animate-pulse">
                      <AlertTriangle className="w-3 h-3 text-amber-700 shrink-0" />
                      BTR STALE (NEEDS RECALCULATION)
                    </span>
                  ) : birthDetails.btrStatus === 'CONFIRMED' ? (
                    <span className="text-[10px] font-mono font-bold px-2 py-1 rounded bg-emerald-100 text-emerald-900 border border-emerald-300 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                      LEGACY BTR — UNVERIFIED
                    </span>
                  ) : birthDetails.isRectified ? (
                    <span className="text-[10px] font-mono font-bold px-2 py-1 rounded bg-amber-50 text-amber-800 border border-amber-300">
                      LEGACY WORKING TIME — UNVERIFIED
                    </span>
                  ) : (
                    <span className="text-[10px] font-mono px-2 py-1 rounded bg-astro-bg text-[#52635F] border border-astro-border">
                      ACTIVE TIME — VERIFY SOURCE
                    </span>
                  )}
                </div>
              </div>

              {/* 4 Epistemic States Pill Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 border-t border-astro-border/70 text-[11px]">
                <div className="p-2 bg-white rounded-lg border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-medium">1. Recorded Birth Time</div>
                  <div className="font-mono font-bold text-astro-primary mt-0.5">{recordedBirthTime || 'Not independently preserved'}</div>
                </div>
                <div className="p-2 bg-white rounded-lg border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-medium">2. Working Time (Not Reverified)</div>
                  <div className="font-mono font-bold text-amber-900 mt-0.5">{workingBirthTime || 'No active time available'}</div>
                </div>
                <div className="p-2 bg-white rounded-lg border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-medium">3. Previously Stored Interval (Unverified)</div>
                  <div className="font-mono font-bold text-astro-text mt-0.5">{birthDetails.rectifiedInterval ? `${birthDetails.rectifiedInterval} — not reverified` : 'Not established'}</div>
                </div>
                <div className="p-2 bg-white rounded-lg border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-medium">4. Previously Stored Confirmation (Unverified)</div>
                  <div className={`font-mono font-bold mt-0.5 ${birthDetails.confirmedTime ? 'text-emerald-800' : 'text-stone-400 italic text-[10px]'}`}>
                    {birthDetails.confirmedTime ? `${birthDetails.confirmedTime} — not reverified` : 'No stored value'}
                  </div>
                </div>
              </div>
            </div>

            {/* BTR STALE Recalculation Notification & Action Card */}
            {birthDetails.btrStatus === 'STALE' && (
              <div className="p-3.5 bg-amber-50 border-2 border-amber-400 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center shrink-0">
                    <RotateCw className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-amber-950 flex items-center gap-1.5">
                      <span>{isHi ? 'आधार समय अद्यतन - बीटीआर परिणाम अमान्य' : 'Base Time Updated — Rectification Stale'}</span>
                    </div>
                    <p className="text-[11px] text-amber-800 leading-tight mt-0.5">
                      {isHi
                        ? 'आधार जन्म समय बदल गया है। V6.5 प्रमाणित इंजन द्वारा पुनर्गणना प्रारंभ करें।'
                        : 'The base birth timestamp changed. Recalculate candidate plateaus with the verified V6.5 engine.'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  id="btn-recalculate-btr"
                  onClick={handleRecalculateBtr}
                  disabled={isRecalculating}
                  className="w-full sm:w-auto px-4 py-2 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white font-bold rounded-lg text-xs transition shadow-sm cursor-pointer flex items-center justify-center gap-1.5 shrink-0 disabled:opacity-50"
                >
                  <RotateCw className={`w-3.5 h-3.5 ${isRecalculating ? 'animate-spin' : ''}`} />
                  <span>{isRecalculating ? (isHi ? 'पुनर्गणना जारी...' : 'Recalculating...') : (isHi ? 'पुनर्गणना करें' : 'Recalculate BTR')}</span>
                </button>
              </div>
            )}

            {recalculationOutcome && (
              <div role="status" className="p-3.5 bg-blue-50 border border-blue-300 rounded-xl text-xs text-blue-950 font-medium">
                {recalculationOutcome}
              </div>
            )}

            {/* Canonical V6.5 Evidence Alignment */}
            {/* ============================================================ */}
            {/* Direct Question-Answer Interview Flow */}
            {/* ============================================================ */}
            <div className="space-y-4 pt-2">
              <div className="bg-astro-bg rounded-xl border border-astro-border p-5 space-y-4 shadow-2xs">
                <div>
                  <h3 className="text-sm font-bold text-astro-primary font-serif flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-astro-accent" />
                    {isHi ? 'एक-एक करके जीवन घटना साक्षात्कार' : 'One-By-One Life Event Interview'}
                  </h3>
                  <p className="text-xs text-astro-secondary-text mt-1">
                    {isHi
                      ? 'विशिष्ट ज्योतिषीय योगों के आधार पर जन्म समय को सटीक करने के लिए नीचे दिए गए प्रश्नों के उत्तर दें।'
                      : 'Record life events as authentic evidence to align against astrological timing triggers.'}
                  </p>
                </div>

                <div role="status" className="p-3.5 bg-blue-50 border border-blue-300 rounded-xl text-xs text-blue-950">
                  <div className="font-bold flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" />{isHi ? 'V6.5 साक्ष्य संरेखण सक्रिय' : 'V6.5 Evidence Alignment Active'}</div>
                  <p className="mt-1">{isHi ? 'जीवन घटनाएँ शास्त्रीय दशा एवं गोचर योगों से जाँची जाती हैं। उम्मीदवार अंतराल की गणना होती है; औपचारिक पुष्टि केवल ज्योतिषी द्वारा की जाती है।' : 'Events are evaluated against classical timing triggers. The V6.5 engine computes continuous plateaus with zero auto-adoption; formal confirmation requires astrologer consultation.'}</p>
                </div>

                  <div className="p-4 bg-[#FAF6ED] rounded-xl border border-astro-border relative overflow-hidden">
                    <span className="text-[10px] font-semibold text-astro-primary tracking-wider uppercase block mb-1">
                      {isHi ? `प्रश्न ${currentQuestionIdx + 1} / ${STRUCTURAL_QUESTIONS.length}` : `Dynamic Question ${currentQuestionIdx + 1} of ${STRUCTURAL_QUESTIONS.length}`}
                    </span>
                    <p className="text-sm font-bold text-astro-text leading-relaxed">
                      {STRUCTURAL_QUESTIONS[currentQuestionIdx].questionText}
                    </p>

                    <div className="mt-4 space-y-3">
                      {/* Precision selector */}
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold text-astro-secondary-text">Precision:</span>
                        {(['Exact', 'Month/Year', 'Year Only'] as const).map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => {
                              setPrecision(p);
                              setDateVal('');
                            }}
                            className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer ${
                              precision === p
                                ? 'bg-astro-primary text-white shadow-2xs'
                                : 'bg-white text-astro-secondary-text border border-astro-border hover:bg-astro-primary/10'
                            }`}
                          >
                            {p === 'Exact' ? 'Exact Date' : p === 'Month/Year' ? 'Month & Year' : 'Year Only'}
                          </button>
                        ))}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-[11px] font-semibold text-astro-secondary-text mb-1">
                            {precision === 'Exact' ? 'Exact Date' : precision === 'Month/Year' ? 'Month & Year (YYYY-MM)' : 'Year Only (YYYY)'}
                          </label>
                          {precision === 'Exact' ? (
                            <input
                              type="date"
                              value={dateVal}
                              onChange={(e) => setDateVal(e.target.value)}
                              className="w-full px-3 py-1.5 border border-astro-border rounded-lg text-xs bg-astro-bg focus:outline-none focus:ring-1 focus:ring-astro-primary text-astro-text"
                            />
                          ) : precision === 'Month/Year' ? (
                            <input
                              type="month"
                              value={dateVal}
                              onChange={(e) => setDateVal(e.target.value)}
                              className="w-full px-3 py-1.5 border border-astro-border rounded-lg text-xs bg-astro-bg focus:outline-none focus:ring-1 focus:ring-astro-primary text-astro-text"
                            />
                          ) : (
                            <input
                              type="number"
                              min="1920"
                              max="2035"
                              placeholder="e.g. 2012"
                              value={dateVal}
                              onChange={(e) => setDateVal(e.target.value)}
                              className="w-full px-3 py-1.5 border border-astro-border rounded-lg text-xs bg-astro-bg focus:outline-none focus:ring-1 focus:ring-astro-primary text-astro-text"
                            />
                          )}
                        </div>

                        <div>
                          <label className="block text-[11px] font-semibold text-astro-secondary-text mb-1">
                            Location / City (Optional)
                          </label>
                          <input
                            type="text"
                            value={locationVal}
                            onChange={(e) => setLocationVal(e.target.value)}
                            placeholder="e.g. New Delhi, Boston"
                            className="w-full px-3 py-1.5 border border-astro-border rounded-lg text-xs bg-astro-bg focus:outline-none focus:ring-1 focus:ring-astro-primary text-astro-text"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-semibold text-astro-secondary-text mb-1">
                            Context Note (Optional)
                          </label>
                          <input
                            type="text"
                            value={descVal}
                            onChange={(e) => setDescVal(e.target.value)}
                            placeholder="e.g. B.Tech Graduation"
                            className="w-full px-3 py-1.5 border border-astro-border rounded-lg text-xs bg-astro-bg focus:outline-none focus:ring-1 focus:ring-astro-primary text-astro-text"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2 justify-end items-center">
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentQuestionIdx(prev => (prev + 1) % STRUCTURAL_QUESTIONS.length);
                          setJustSaved(false);
                          setDateVal('');
                          setLocationVal('');
                          setDescVal('');
                          setInitialQuestionState({
                            dateVal: '',
                            locationVal: '',
                            descVal: '',
                            precision: 'Month/Year'
                          });
                        }}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                          justSaved
                            ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-md animate-bounce ring-2 ring-amber-300'
                            : 'border border-astro-border bg-astro-bg text-astro-secondary-text hover:bg-[#FAF6ED] hover:text-astro-primary'
                        }`}
                      >
                        {justSaved ? '✨ Next Question →' : (isHi ? 'छोड़ें / अगला प्रश्न' : 'Skip / Next Question')}
                      </button>
                      <button
                        type="button"
                        onClick={handleAddEventFromQuestion}
                        className="px-3.5 py-1.5 bg-astro-primary hover:bg-astro-secondary text-white font-bold rounded-lg text-xs transition shadow-2xs cursor-pointer"
                      >
                        {isHi ? 'घटना सहेजें' : 'Save Event'}
                      </button>
                    </div>
                  </div>

                  {isProOrAcharyaOrAdmin && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-astro-text text-xs">
                        {isHi ? 'सक्रिय प्रश्न के लिए ज्योतिषीय लक्ष्य:' : 'Astrological Targets for Active Question:'}
                      </h4>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="p-2.5 bg-[#FAF6ED] rounded-lg border border-astro-border">
                          <span className="text-[10px] text-astro-secondary-text block">House Involvement</span>
                          <strong className="text-xs text-astro-text">
                            {STRUCTURAL_QUESTIONS[currentQuestionIdx].houses.map(h => formatOrdinal(h)).join(', ')}
                          </strong>
                        </div>
                        <div className="p-2.5 bg-[#FAF6ED] rounded-lg border border-astro-border">
                          <span className="text-[10px] text-astro-secondary-text block">Karakas</span>
                          <strong className="text-xs text-astro-text">
                            {STRUCTURAL_QUESTIONS[currentQuestionIdx].planets.join(', ')}
                          </strong>
                        </div>
                        <div className="p-2.5 bg-[#FAF6ED] rounded-lg border border-astro-border">
                          <span className="text-[10px] text-astro-secondary-text block">Varga Focus</span>
                          <strong className="text-xs text-astro-text">
                            {STRUCTURAL_QUESTIONS[currentQuestionIdx].vargas.join(', ')}
                          </strong>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="p-3 bg-[#FAF6ED] rounded-xl border border-astro-border space-y-1">
                    <div className="flex items-center justify-between text-xs font-bold text-astro-primary">
                      <span>{isHi ? `पंजीकृत घटनाएँ (${events.length})` : `Registered Anchor Events (${events.length})`}</span>
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {events.map((evt) => (
                        <div key={evt.id} className="flex items-center justify-between p-1.5 bg-astro-bg rounded border border-astro-border text-xs">
                          <div>
                            <span className="font-semibold text-astro-text">{evt.category}:</span> <span className="text-astro-secondary-text">{evt.description} ({evt.date})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-emerald-700 font-bold text-[10px]">✓ Active</span>
                            <button
                              type="button"
                              onClick={() => {
                                // Non-destructive change: set active question and pre-populate input fields
                                const qIdx = STRUCTURAL_QUESTIONS.findIndex(q => q.category === evt.category);
                                if (qIdx !== -1) {
                                  setCurrentQuestionIdx(qIdx);
                                }
                                const p = (evt.datePrecision === 'Exact' || evt.datePrecision === 'Month/Year' || evt.datePrecision === 'Year Only')
                                  ? evt.datePrecision
                                  : 'Month/Year';
                                setPrecision(p);

                                let d = evt.date;
                                if (p === 'Year Only' && d) {
                                  d = d.split('-')[0];
                                } else if (p === 'Month/Year' && d) {
                                  const parts = d.split('-');
                                  d = parts.length >= 2 ? `${parts[0]}-${parts[1]}` : d;
                                }

                                setDateVal(d || '');
                                setLocationVal(evt.location || '');
                                setDescVal(evt.description || '');
                                setInitialQuestionState({
                                  dateVal: d || '',
                                  locationVal: evt.location || '',
                                  descVal: evt.description || '',
                                  precision: p
                                });
                                setJustSaved(false);
                              }}
                              className="text-[10px] text-amber-700 hover:text-amber-900 font-bold px-2 py-0.5 rounded bg-amber-50 border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                            >
                              Change Response
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
              </div>
            </div>
          </div>

          {/* Exit Confirmation Modal */}
          {showExitConfirm && (
            <div className="absolute inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl border border-astro-border p-6 max-w-sm w-full shadow-2xl space-y-4">
                <h3 className="text-base font-bold text-astro-primary font-serif flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  {isHi ? 'क्या आप बाहर निकलना चाहते हैं?' : 'Unsaved Changes or Rectification Progress'}
                </h3>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {hasChanges
                    ? (isHi
                        ? 'वर्तमान प्रश्न में आपके पास बिना सहेजे बदलाव हैं। यदि आप बिना सहेजे बाहर निकलते हैं, तो ये संपादन रद्द हो जाएंगे।'
                        : 'You have unconfirmed changes in the active question form. Discarding will lose these changes.')
                    : (isHi
                        ? 'जीवन घटनाएँ जन्म समय से अलग रखी जाती हैं। बाहर निकलने से जन्म समय नहीं बदलेगा।'
                        : 'Event responses are kept separate from the birth time. Closing will not change the birth time.')}
                </p>
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowExitConfirm(false);
                      onClose();
                    }}
                    className="px-3 py-1.5 border border-astro-border text-astro-secondary-text rounded-lg text-xs font-semibold hover:bg-gray-50 cursor-pointer"
                  >
                    {isHi ? 'रद्द करें और निकलें' : 'Discard & Exit'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowExitConfirm(false)}
                    className="px-4 py-1.5 bg-astro-primary hover:bg-astro-secondary text-white font-bold rounded-lg text-xs transition cursor-pointer shadow-sm"
                  >
                    {isHi ? 'जारी रखें' : 'Continue Editing'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default BtrDrawer;
